import { useEffect, useRef } from "react";
import "./Hero.css";

// ✅ import from assets
import heroBg from "../../assets/hero-bg.png"; // update path if needed

export default function Hero() {
  const heroRef = useRef(null);
  const tiltRef = useRef(null);

  const go = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const ripple = (e) => {
    const btn = e.currentTarget;
    const r = btn.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * 100;
    const y = ((e.clientY - r.top) / r.height) * 100;

    btn.style.setProperty("--px", `${x}%`);
    btn.style.setProperty("--py", `${y}%`);

    btn.classList.remove("is-rippling");
    // force reflow
    // eslint-disable-next-line no-unused-expressions
    btn.offsetWidth;
    btn.classList.add("is-rippling");
  };

  useEffect(() => {
    const section = heroRef.current;
    if (!section) return;

    // mount (for transition-ready state)
    const raf = requestAnimationFrame(() => section.classList.add("is-mounted"));

    // reveal once when section is visible
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          section.classList.add("is-visible");
          io.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    io.observe(section);

    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
    };
  }, []);

  // ✅ Desktop-only: subtle tilt + background parallax (professional)
  useEffect(() => {
    const section = heroRef.current;
    const tilt = tiltRef.current;
    if (!section || !tilt) return;

    const canAnimate =
      window.matchMedia("(hover: hover) and (pointer: fine)").matches;

    if (!canAnimate) return;

    const maxTilt = 6; // degrees (small = professional)
    const maxShift = 3; // background shift in %

    const onMove = (e) => {
      const rect = tilt.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width; // 0..1
      const y = (e.clientY - rect.top) / rect.height; // 0..1

      const ry = (x - 0.5) * (maxTilt * 2);
      const rx = (0.5 - y) * (maxTilt * 2);

      tilt.style.setProperty("--rx", rx.toFixed(2));
      tilt.style.setProperty("--ry", ry.toFixed(2));

      // background parallax
      const bgx = 50 + (x - 0.5) * (maxShift * 2);
      const bgy = 50 + (y - 0.5) * (maxShift * 2);
      section.style.setProperty("--bgx", `${bgx.toFixed(2)}%`);
      section.style.setProperty("--bgy", `${bgy.toFixed(2)}%`);
    };

    const onLeave = () => {
      tilt.style.setProperty("--rx", "0");
      tilt.style.setProperty("--ry", "0");
      section.style.setProperty("--bgx", "50%");
      section.style.setProperty("--bgy", "50%");
    };

    section.addEventListener("pointermove", onMove, { passive: true });
    section.addEventListener("pointerleave", onLeave, { passive: true });

    return () => {
      section.removeEventListener("pointermove", onMove);
      section.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  return (
    <section
      id="home"
      className="hero"
      ref={heroRef}
      style={{ "--hero-bg": `url(${heroBg})` }} // ✅ pass image to CSS
    >
      <div className="container hero__inner hero__single">
        <div className="hero__left hero__tilt" ref={tiltRef}>
          <span className="kicker reveal" style={{ "--d": "0ms" }}>
            <span className="dot" /> Performance • Content • Branding
          </span>

          <h1 className="hero__title reveal" style={{ "--d": "90ms" }}>
            Grow faster with{" "}
            <span className="hero__grad">smart digital marketing</span>
          </h1>

          <p className="hero__sub reveal" style={{ "--d": "180ms" }}>
            We build campaigns that bring leads, sales, and strong brand presence.
            Simple process. Clear reporting. Real results.
          </p>

          <div className="hero__actions reveal" style={{ "--d": "270ms" }}>
            <button
              className="btn btn-primary"
              onPointerDown={ripple}
              onClick={() => go("#contact")}
            >
              Get Free Strategy Call
            </button>
            <button className="btn" onPointerDown={ripple} onClick={() => go("#work")}>
              See Our Work
            </button>
          </div>

        </div>
      </div>
    </section>
  );
}
